import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { navigationType } from 'app/app.config';
import { navigationItems } from 'app/core/navigation/navigation.data';
import { Navigation } from 'app/core/navigation/navigation.types';
import { cloneDeep } from 'lodash';
import { combineLatest, map, Observable, of, ReplaySubject, take } from 'rxjs';
import { UserService } from '../user/user.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        if (navigationType === 'Static') {
            const navigation = {
                compact: navigationItems,
                default: navigationItems,
                futuristic: navigationItems,
                horizontal: navigationItems,
            };
            this._navigation.next(navigation);
            return of(navigation);
        } else {
            // Get current user data (should be available now since resolver loads user first)
            return combineLatest([
                this._userService.getPermissions(),
                this._userService.user$,
            ]).pipe(
                take(1),
                map(([permissions, user]) => {
                    const filterByPermissions = (
                        items: FuseNavigationItem[]
                    ) => {
                        return items
                            .map((item) => {
                                // Recursively filter children if they exist
                                if (item.children) {
                                    item.children = filterByPermissions(
                                        item.children
                                    );
                                }
                                return item;
                            })
                            .filter((item) => {
                                if (
                                    Array.isArray(item.children) &&
                                    item.children.length === 0
                                )
                                    return false; // Exclude items with empty children
                                if (
                                    !item.meta ||
                                    !item.meta.permission ||
                                    user.role === 'Admin'
                                ) {
                                    return true; // No permissions required, include item
                                }
                                return permissions.includes(
                                    item.meta.permission
                                );
                            });
                    };

                    const filteredNavigationItems = user
                        ? filterByPermissions(cloneDeep(navigationItems))
                        : [];

                    const navigation = {
                        compact: filteredNavigationItems,
                        default: filteredNavigationItems,
                        futuristic: filteredNavigationItems,
                        horizontal: filteredNavigationItems,
                    };
                    this._navigation.next(navigation);
                    return navigation;
                })
            );
        }
    }

    /**
     * Force refresh navigation based on current user
     */
    refreshNavigation(): Observable<Navigation> {
        return this.get();
    }
}
